import React from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiShield, FiAlertTriangle, FiCheckCircle, FiEye, FiTrendingUp, FiSettings, FiTarget } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MobileFeatureCarousel from '../components/MobileFeatureCarousel';
import ImageModal from '../components/ImageModal';

const SAST = () => {
  const [imageModal, setImageModal] = React.useState<{
    isOpen: boolean;
    src: string;
    alt: string;
    title?: string;
  }>({
    isOpen: false,
    src: '',
    alt: '',
    title: ''
  });

  const openImageModal = (src: string, alt: string, title?: string) => {
    setImageModal({ isOpen: true, src, alt, title });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, src: '', alt: '', title: '' });
  };

  const currentStats = [
    { label: "Total Scans", value: "19", description: "SAST scans completed", icon: <FiCode className="w-8 h-8" /> },
    { label: "Identified Fixes", value: "61", description: "security issues identified", icon: <FiAlertTriangle className="w-8 h-8" /> },
    { label: "Completed Fixes", value: "91", description: "issues resolved", icon: <FiCheckCircle className="w-8 h-8" /> },
    { label: "Last Scan", value: "2", description: "days ago", icon: <FiTrendingUp className="w-8 h-8" /> }
  ];

  const coreFeatures = [
    {
      title: "Static Code Analysis",
      description: "Comprehensive analysis of source code without execution, supporting multiple languages and advanced pattern recognition.",
      icon: <FiCode className="w-8 h-8" />,
      details: [
        "Source Code Scanning: Comprehensive analysis of source code without execution",
        "Multi-Language Support: Support for Java, C#, JavaScript, Python, and more",
        "Pattern Recognition: Advanced pattern matching for security vulnerabilities",
        "Control Flow Analysis: Analysis of code execution paths and data flows"
      ]
    },
    {
      title: "Vulnerability Detection",
      description: "Advanced detection of security vulnerabilities including OWASP Top 10 and CWE coverage with custom rules support.",
      icon: <FiTarget className="w-8 h-8" />,
      details: [
        "OWASP Top 10: Detection of OWASP Top 10 security vulnerabilities",
        "CWE Coverage: Comprehensive coverage of Common Weakness Enumeration",
        "Custom Rules: Support for custom security rules and patterns",
        "Zero-Day Detection: Identification of previously unknown vulnerabilities"
      ]
    },
    {
      title: "Security Assessment",
      description: "Automated risk assessment with severity classification, impact analysis, and detailed remediation guidance.",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Risk Scoring: Automated risk assessment and severity classification",
        "Impact Analysis: Assessment of potential security impact",
        "Remediation Guidance: Detailed guidance for fixing identified issues",
        "Compliance Mapping: Mapping to security standards and regulations"
      ]
    }
  ];

  const supportedLanguages = [
    { name: "Java", description: "Complete Java ecosystem support including Spring, Struts", icon: "☕" },
    { name: "C#/.NET", description: ".NET Framework and .NET Core applications", icon: "🔷" },
    { name: "JavaScript/TypeScript", description: "Node.js, React, Angular, Vue.js applications", icon: "🟨" },
    { name: "Python", description: "Django, Flask, and other Python frameworks", icon: "🐍" },
    { name: "C/C++", description: "Native C/C++ applications and libraries", icon: "⚡" },
    { name: "PHP", description: "Laravel, Symfony, and other PHP frameworks", icon: "🐘" },
    { name: "Ruby", description: "Ruby on Rails and other Ruby applications", icon: "💎" },
    { name: "Go", description: "Go applications and microservices", icon: "🐹" },
    { name: "Swift", description: "iOS and macOS applications", icon: "🍎" },
    { name: "Kotlin", description: "Android and JVM-based applications", icon: "📱" }
  ];

  const vulnerabilityCategories = [
    {
      category: "Injection Vulnerabilities",
      description: "Detection of various injection attack vectors",
      icon: <FiTarget className="w-8 h-8" />,
      details: [
        "SQL Injection: Detection of SQL injection vulnerabilities",
        "Command Injection: OS command injection detection",
        "LDAP Injection: LDAP query injection vulnerabilities",
        "XPath Injection: XPath query injection detection"
      ]
    },
    {
      category: "Authentication & Authorization",
      description: "Security issues related to user authentication and access control",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "Broken Authentication: Weak authentication mechanisms",
        "Session Management: Insecure session handling",
        "Access Control: Broken access control vulnerabilities",
        "Privilege Escalation: Unauthorized privilege elevation"
      ]
    },
    {
      category: "Data Protection",
      description: "Vulnerabilities related to sensitive data handling and protection",
      icon: <FiEye className="w-8 h-8" />,
      details: [
        "Sensitive Data Exposure: Unprotected sensitive information",
        "Cryptographic Issues: Weak or broken cryptography",
        "Data Validation: Input validation vulnerabilities",
        "Information Disclosure: Unintended information leakage"
      ]
    },
    {
      category: "Code Quality Issues",
      description: "Security-related code quality and memory management issues",
      icon: <FiCode className="w-8 h-8" />,
      details: [
        "Buffer Overflows: Memory corruption vulnerabilities",
        "Race Conditions: Concurrency-related security issues",
        "Resource Leaks: Memory and resource management issues",
        "Error Handling: Improper error handling and logging"
      ]
    }
  ];

  const analysisResults = [
    {
      category: "Security Vulnerabilities (61 Identified)",
      description: "Comprehensive breakdown of identified security issues",
      icon: <FiAlertTriangle className="w-8 h-8" />,
      details: [
        "Critical Issues: High-severity vulnerabilities requiring immediate attention",
        "High-Risk Issues: Significant security concerns with potential impact",
        "Medium-Risk Issues: Moderate security issues requiring remediation",
        "Low-Risk Issues: Minor security concerns and code quality improvements"
      ]
    },
    {
      category: "Remediation Status (91 Completed)",
      description: "Current status of vulnerability remediation efforts",
      icon: <FiCheckCircle className="w-8 h-8" />,
      details: [
        "Fixed Issues: Successfully resolved security vulnerabilities",
        "In Progress: Issues currently being addressed by development teams",
        "Pending Review: Fixes awaiting security team validation",
        "False Positives: Identified false positives requiring exclusion"
      ]
    },
    {
      category: "Code Quality Metrics",
      description: "Overall code security and quality assessment metrics",
      icon: <FiTrendingUp className="w-8 h-8" />,
      details: [
        "Security Score: Overall security posture assessment",
        "Code Coverage: Percentage of code analyzed for security issues",
        "Technical Debt: Security-related technical debt assessment",
        "Compliance Status: Adherence to security coding standards"
      ]
    }
  ];

  const integrationCapabilities = [
    {
      title: "Development Workflow Integration",
      description: "Seamless integration with development tools and processes",
      icon: <FiCode className="w-8 h-8" />,
      details: [
        "IDE Integration: Real-time security analysis in development environments",
        "CI/CD Pipeline: Automated security scanning in build processes",
        "Git Integration: Pre-commit and pull request security checks",
        "Build Tools: Integration with Maven, Gradle, npm, and other build systems"
      ]
    },
    {
      title: "Security Tool Integration",
      description: "Integration with enterprise security and monitoring tools",
      icon: <FiShield className="w-8 h-8" />,
      details: [
        "SIEM Integration: Security event correlation and monitoring",
        "Vulnerability Management: Integration with vulnerability tracking systems",
        "Issue Tracking: Automatic issue creation in Jira, GitHub, and other systems",
        "Reporting Tools: Integration with security reporting and analytics platforms"
      ]
    },
    {
      title: "Enterprise Integration",
      description: "Enterprise-grade authentication and API integration capabilities",
      icon: <FiSettings className="w-8 h-8" />,
      details: [
        "Active Directory: Enterprise authentication and authorization",
        "LDAP Integration: Directory service integration for user management",
        "SSO Support: Single sign-on integration with enterprise systems",
        "API Integration: RESTful APIs for custom integrations and automation"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "SAST Implementation",
      description: "Best practices for implementing and optimizing SAST processes",
      icon: <FiSettings className="w-8 h-8" />,
      details: [
        "Early Integration: Integrate SAST early in the development lifecycle",
        "Regular Scanning: Implement regular and automated security scanning",
        "Developer Training: Train developers on secure coding practices",
        "Custom Rules: Develop custom rules for organization-specific security requirements",
        "False Positive Management: Implement effective false positive management processes"
      ]
    },
    {
      category: "Vulnerability Management",
      description: "Effective strategies for managing and remediating vulnerabilities",
      icon: <FiTarget className="w-8 h-8" />,
      details: [
        "Risk Prioritization: Prioritize vulnerabilities based on risk and impact",
        "Rapid Remediation: Establish rapid response procedures for critical vulnerabilities",
        "Verification Testing: Verify fixes through re-scanning and testing",
        "Documentation: Maintain comprehensive vulnerability documentation",
        "Metrics Tracking: Track security metrics and improvement trends"
      ]
    },
    {
      category: "Process Optimization",
      description: "Continuous improvement of SAST processes and workflows",
      icon: <FiTrendingUp className="w-8 h-8" />,
      details: [
        "Workflow Integration: Seamlessly integrate SAST into development workflows",
        "Automated Reporting: Implement automated security reporting and notifications",
        "Continuous Improvement: Continuously improve SAST processes and coverage",
        "Tool Tuning: Regularly tune SAST tools for optimal accuracy and performance",
        "Stakeholder Communication: Maintain clear communication with all stakeholders"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Static Application Security Testing (SAST)" />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
            The SAST module in Ana Security provides comprehensive static code analysis capabilities to identify security vulnerabilities, coding flaws, and compliance issues in source code without executing the application.
          </p>
          
          <div className="relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900 mb-8">
            <img 
              src="/screenshots/security-modules/08_sast.png" 
              alt="SAST Dashboard"
              className="w-full cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => openImageModal('/screenshots/security-modules/08_sast.png', 'SAST Dashboard', 'Static Application Security Testing Dashboard')}
            />
          </div>
        </div>

        {/* Current Statistics - Responsive */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SAST Dashboard - Current Statistics
          </h2>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {currentStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
              >
                <div className="text-primary-500 mb-3 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden mb-8">
            <MobileFeatureCarousel 
              features={currentStats.map(stat => ({
                title: stat.label,
                description: stat.description,
                icon: stat.icon,
                details: [`Current Value: ${stat.value}`, stat.description]
              }))}
              autoPlay={true}
              autoPlayInterval={4000}
            />
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Dashboard Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new SAST analyses</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> "All" dropdown for filtering by specific projects</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Scan History:</strong> Complete history of all SAST scanning activities</span>
              </div>
              <div className="flex items-start gap-3">
                <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300"><strong>Status Tracking:</strong> Real-time status updates for ongoing scans</span>
              </div>
            </div>
          </div>
        </div>

        {/* Core Features - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SAST Core Features
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={coreFeatures}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Supported Languages - Responsive Grid */}
        <div className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Supported Programming Languages
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-6">
            SAST provides comprehensive security analysis across multiple programming languages and frameworks:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {supportedLanguages.map((language, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{language.icon}</span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {language.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Vulnerability Categories - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Vulnerability Detection Categories
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {vulnerabilityCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {category.category}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {category.details?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <FiTarget className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={vulnerabilityCategories.map(category => ({
                title: category.category,
                description: category.description,
                icon: category.icon,
                details: category.details
              }))}
              autoPlay={true}
              autoPlayInterval={7000}
            />
          </div>
        </div>

        {/* Analysis Results - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SAST Analysis Results
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {analysisResults.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {category.category}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {category.details?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <FiEye className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={analysisResults.map(category => ({
                title: category.category,
                description: category.description,
                icon: category.icon,
                details: category.details
              }))}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Integration Capabilities - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Integration Capabilities
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {integrationCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {capability.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {capability.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {capability.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {capability.details?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={integrationCapabilities}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Best Practices - Responsive */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            SAST Best Practices
          </h2>
          
          {/* Desktop Layout */}
          <div className="hidden md:block space-y-6">
            {bestPractices.map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-500">
                    {practice.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {practice.category}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {practice.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {practice.details?.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <MobileFeatureCarousel 
              features={bestPractices.map(practice => ({
                title: practice.category,
                description: practice.description,
                icon: practice.icon,
                details: practice.details
              }))}
              autoPlay={true}
              autoPlayInterval={6000}
            />
          </div>
        </div>

        {/* Call to Action - Responsive */}
        <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Secure Your Source Code?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8">
            Start using SAST to identify and fix security vulnerabilities in your source code before deployment.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.a 
              href="/ana-actions" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiCheckCircle className
="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Automatically fix code vulnerabilities</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/dashboard" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiTrendingUp className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor security metrics</p>
              </div>
            </motion.a>
            
            <motion.a 
              href="/projects" 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 p-4 bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500/30 transition-colors"
            >
              <FiCode className="w-6 h-6 text-primary-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Manage Projects</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Configure SAST for your projects</p>
              </div>
            </motion.a>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={imageModal.isOpen}
        src={imageModal.src}
        alt={imageModal.alt}
        title={imageModal.title}
        onClose={closeImageModal}
      />
    </div>
  );
};

export default SAST;
